'use client';

import { Button, resolveButtonCtaTheme } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import InfoIconModal from '@/components/infoIcon/InfoIconModal';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import ModalBox from '@/components/modalBox/ModalBox';
import ProductCardBenefitsList from '@/components/productCardBenefitsList/ProductCardBenefitsList';
import { RichText } from '@/components/richText/RichText';
import { getBulletPointList } from '@/helper/RichText';
import { resolveColorKey } from '@/helper/color';
import { appendHrefQuery, linkToString } from '@/helper/urls';
import Leaf from '@/public/icons/leaf.svg';
import type { MultilinkStoryblok, PartialBusinessProductCardStoryblok } from '@/types/component-types-sb';
import { storyblokEditable } from '@storyblok/react/rsc';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './BusinessProductCard.module.scss';

interface BusinessProductCardProps {
  blok: PartialBusinessProductCardStoryblok;
  groupLink?: MultilinkStoryblok;
  groupFormProductFieldName?: string;
}

const BusinessProductCard: React.FC<BusinessProductCardProps> = ({ blok, groupLink, groupFormProductFieldName }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const toggleOpen = () => {
    setOpen(!open);
  };

  const benefitsList = getBulletPointList(blok.offer_description);
  const linkFormProductFieldName = blok.formProductFieldName ?? groupFormProductFieldName ?? 'productName';

  // Try card link first, fall back to group link if card link is empty
  const cardLinkPath = linkToString(blok.link);
  const linkObj: MultilinkStoryblok | undefined = (cardLinkPath ? blok.link : groupLink) as
    MultilinkStoryblok | undefined;

  const pathAndQuery = linkToString(linkObj);

  const productTitle = `${blok.subheadline} ${blok.headline}`;
  const link = pathAndQuery
    ? appendHrefQuery(
        pathAndQuery,
        `${linkFormProductFieldName}=${encodeURIComponent(productTitle)}${blok.eco_tariff ? '&eco_tariff=true' : ''}`
      )
    : null;

  // Extract target attribute from Storyblok link (e.g., "_blank" for "open in tab")
  const target = typeof linkObj === 'object' && linkObj !== null ? linkObj.target : undefined;
  const ctaLabel = t('Jetzt anfragen');

  return (
    <div
      {...storyblokEditable(blok)}
      className={classNames(classes.businessProductCard)}
      data-testid="business-product-card"
      data-theme={resolveColorKey(blok.color.name)}
    >
      <div className={classes.headerBlock}>
        <HeadlineField tag={HeadlineTag.H3} className={classes.subheader}>
          {blok.subheadline}
        </HeadlineField>
        <HeadlineField tag={HeadlineTag.H3} className={classes.header}>
          {blok.headline}
        </HeadlineField>
      </div>

      <div className={classes.priceBlok}>
        <p className={classes.teaser}>{blok.teaser_text}</p>
        {blok.badge_text && (
          <div className={classes.badge}>
            {blok.eco_tariff && <Leaf aria-hidden="true" />}
            <span className={classes.badgeText}>{blok.badge_text}</span>
            <InfoIconModal theme="whiteGreen" trackingLabel="Benefit">
              <RichText text={blok.badge_content} />
            </InfoIconModal>
          </div>
        )}
      </div>

      <p className={classes.description}>{blok.description}</p>

      <ProductCardBenefitsList
        benefits={
          benefitsList?.map((item) => {
            return {
              label: item,
            };
          }) ?? []
        }
      />

      <div className={classes.detailsRow}>
        {blok.link_name && (
          <ModalBox
            data-theme={resolveColorKey(blok.color.name)}
            button={
              <InlineTextLink
                data-testid="product-card-modalBox-tariffDetails"
                onClick={toggleOpen}
                className={classes.buttonModal}
              >
                {blok.link_name}
              </InlineTextLink>
            }
            toggleOpen={toggleOpen}
            large={true}
            open={open}
          >
            <RichText text={blok.link_details} />
          </ModalBox>
        )}
      </div>

      <Button
        {...(link && { href: link, target })}
        variant="ctaFull"
        data-theme={resolveButtonCtaTheme(blok.color?.name)}
        data-testid="business-product-card-button"
        disabled={!link}
        label={ctaLabel}
      />
    </div>
  );
};

export default BusinessProductCard;
